import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Calendar,
  ChevronRight,
  LogIn,
  MapPin,
  Navigation,
  RefreshCw,
  Ticket,
  X,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { EmptyState, Img, Shimmer } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { cancelBookingById, getMyBooking } from '@/api/apiUtils';
import { toast } from 'react-hot-toast';
import {
  STATUS_STYLES,
  canCancelBooking,
  bucketOf,
  formatWhen,
  normalizeBooking,
  prettyStatus,
} from '@/lib/bookings';
import { formatINR } from '@/lib/catalog';

const TABS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const BookingCard = ({ booking, onCancel }) => {
  const canCancel = canCancelBooking(booking);
  const mapHref =
    booking.latitude && booking.longitude
      ? `https://www.google.com/maps/dir/?api=1&destination=${booking.latitude},${booking.longitude}`
      : null;

  return (
    <motion.article
      layout
      // No opacity-0 start: a booking card must never depend on a JS
      // animation finishing to become visible. Layout/exit still animate.
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
      className="lk-card overflow-hidden transition-shadow hover:shadow-cardHover"
    >
      <div className="p-5">
        <div className="flex gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-bold text-ink-900">{booking.title}</h3>
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-ink-700">
              <Calendar className="h-3.5 w-3.5 shrink-0 text-ink-500" aria-hidden="true" />
              {formatWhen(booking.date, booking.time)}
            </p>
            <p className="mt-0.5 text-sm text-ink-500">
              {booking.ticketCount > 0
                ? `${booking.ticketCount} ticket${booking.ticketCount === 1 ? '' : 's'}`
                : booking.category}
            </p>
            {booking.balanceDue > 0 && !['cancelled', 'payment_failed'].includes(booking.status) && (
              <span className="mt-1.5 inline-flex rounded-full bg-warning-tint px-2 py-0.5 text-xs font-semibold text-warning">
                Balance due ₹{Number(booking.balanceDue).toLocaleString('en-IN')}
              </span>
            )}
          </div>

          <Img
            src={booking.image}
            alt={booking.title}
            className="h-24 w-[74px] shrink-0 rounded-xl"
          />
        </div>

        {(booking.venue || booking.city) && (
          <div className="mt-4 flex items-start justify-between gap-3 border-t border-ink-200/70 pt-4">
            <div className="min-w-0">
              <p className="lk-label">Location</p>
              <p className="mt-0.5 flex items-start gap-1.5 text-sm text-ink-900">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-400" />
                <span className="line-clamp-2">
                  {[booking.venue, booking.city].filter(Boolean).join(', ')}
                </span>
              </p>
            </div>
            {mapHref && (
              <a
                href={mapHref}
                target="_blank"
                rel="noreferrer"
                aria-label="Directions to the venue"
                className="lk-icon-btn"
              >
                <Navigation className="h-4 w-4" />
              </a>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-ink-200/70 bg-surface-muted/60 px-5 py-3">
        <div className="flex items-center gap-3">
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${
              STATUS_STYLES[booking.status] || 'bg-surface-sunken text-ink-700 ring-ink-200'
            }`}
          >
            {prettyStatus(booking.status)}
          </span>
          <span className="tabular text-sm font-bold text-ink-900">₹{formatINR(booking.amount)}</span>
        </div>

        <div className="ml-auto flex items-center gap-1">
          {canCancel && (
            <button
              type="button"
              onClick={() => onCancel(booking)}
              className="lk-focus min-h-[40px] rounded-lg px-3 text-sm font-medium text-danger transition-colors hover:bg-danger-tint"
            >
              Cancel
            </button>
          )}
          <Link
            to={`/booking/${booking.id}`}
            className="lk-focus flex min-h-[40px] items-center gap-1 whitespace-nowrap rounded-lg px-3 text-sm font-semibold text-ink-900 transition-colors hover:bg-white"
          >
            View details
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

const CancelModal = ({ booking, onClose, onConfirm, submitting }) => {
  const [reason, setReason] = useState('');
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-2xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50">
            <AlertCircle className="h-5 w-5 text-red-600" />
          </span>
          <div>
            <h3 className="text-lg font-semibold text-ink-900">Cancel this booking?</h3>
            <p className="mt-1 text-sm text-ink-500">
              {booking?.title}. Any refund follows the organiser's cancellation policy.
            </p>
          </div>
        </div>

        <label htmlFor="cancel-reason" className="mb-1.5 mt-5 block text-sm font-medium text-ink-700">
          Reason (optional)
        </label>
        <textarea
          id="cancel-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Let us know why you're cancelling…"
          className="w-full rounded-xl border border-ink-200 px-3 py-2 text-base outline-none focus:border-brand-secondary md:text-sm"
        />

        <div className="mt-5 flex gap-3">
          <button type="button" onClick={onClose} disabled={submitting} className="lk-btn-ghost flex-1">
            Keep booking
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason)}
            disabled={submitting}
            className="lk-btn flex-1 bg-red-600 text-white hover:bg-red-700"
          >
            {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
            Cancel booking
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const MyBookings = () => {
  const { user, openAuthModal } = useAuth();
  const [tab, setTab] = useState('upcoming');
  const [category, setCategory] = useState('all');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const res = await getMyBooking({});
      const payload = res?.data;
      const list = Array.isArray(payload) ? payload : payload?.data || [];
      setBookings(list.map(normalizeBooking).filter((b) => b.id != null));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // Category pills are built from the bookings themselves, so they always match
  // whatever verticals the customer has actually used.
  const categories = useMemo(() => {
    const names = new Set();
    bookings.forEach((b) => b.category && names.add(b.category));
    return ['all', ...Array.from(names).sort()];
  }, [bookings]);

  const scoped = useMemo(
    () => (category === 'all' ? bookings : bookings.filter((b) => b.category === category)),
    [bookings, category]
  );

  const grouped = useMemo(() => {
    const groups = { upcoming: [], completed: [], cancelled: [] };
    scoped.forEach((b) => groups[bucketOf(b.status, b)].push(b));
    Object.values(groups).forEach((list) =>
      list.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
    );
    return groups;
  }, [scoped]);

  const confirmCancel = async (reason) => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelBookingById(cancelTarget.id, { reason: reason || 'Cancelled by user' });
      setCancelTarget(null);
      await load();
    } catch (err) {
      const body = err?.response?.data;
      if (err?.response?.status === 403 && (body?.code === 'EVENT_CANCEL_BLOCKED' || body?.errors?.code === 'EVENT_CANCEL_BLOCKED')) {
        // Event tickets can't be cancelled by the customer.
        setCancelTarget(null);
        toast.error(body?.message || "Event tickets can't be cancelled here — open the booking and tap Get help.");
      } else {
        setError(true);
      }
    } finally {
      setCancelling(false);
    }
  };

  const list = grouped[tab];

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />

      <main className="flex-1">
        <div className="border-b border-ink-200/70 bg-white">
          <div className="lk-container py-8">
            <h1 className="lk-heading">My Bookings</h1>
            <p className="mt-1 text-sm text-ink-500">
              Your tickets, receipts and QR codes — all in one place.
            </p>
          </div>
        </div>

        <div className="lk-container py-8">
          {!user ? (
            <EmptyState
              icon={LogIn}
              title="Sign in to see your bookings"
              message="Log in to view, manage and cancel your event bookings."
              action={
                <button type="button" onClick={openAuthModal} className="lk-btn-primary">
                  Log in
                </button>
              }
            />
          ) : (
            <>
              {/* Vertical filter */}
              {categories.length > 2 && (
                <div className="scrollbar-hide mb-5 flex gap-2 overflow-x-auto pb-1">
                  {categories.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setCategory(name)}
                      className={`lk-chip ${category === name ? 'lk-chip-active' : 'lk-chip-idle'}`}
                    >
                      {name === 'all' ? 'All' : name}
                    </button>
                  ))}
                </div>
              )}

              {/* Status tabs */}
              <div className="mb-6 inline-flex rounded-full bg-surface-sunken p-1">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                      tab === t.key ? 'bg-brand-primary text-white shadow-card' : 'text-ink-700 hover:text-ink-900'
                    }`}
                  >
                    {t.label}
                    {!loading && grouped[t.key].length > 0 && (
                      <span className={`ml-1.5 text-xs ${tab === t.key ? 'text-white/70' : 'text-ink-400'}`}>
                        {grouped[t.key].length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {loading ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Shimmer key={i} className="h-56 rounded-2xl" />
                  ))}
                </div>
              ) : error ? (
                <EmptyState
                  icon={AlertCircle}
                  title="Something went wrong"
                  message="We couldn't load your bookings just now."
                  action={
                    <button type="button" onClick={load} className="lk-btn-primary">
                      <RefreshCw className="h-4 w-4" /> Try again
                    </button>
                  }
                />
              ) : list.length === 0 ? (
                <EmptyState
                  icon={Ticket}
                  title={`No ${tab} bookings`}
                  message="Discover events happening around you."
                  action={
                    <Link to="/" className="lk-btn-primary">
                      Explore events <ChevronRight className="h-4 w-4" />
                    </Link>
                  }
                />
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <AnimatePresence mode="popLayout">
                    {list.map((booking) => (
                      <BookingCard key={booking.id} booking={booking} onCancel={setCancelTarget} />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />

      <AnimatePresence>
        {cancelTarget && (
          <CancelModal
            booking={cancelTarget}
            submitting={cancelling}
            onClose={() => !cancelling && setCancelTarget(null)}
            onConfirm={confirmCancel}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyBookings;
